(function () {
    'use strict';

    angular
        .module('salesflare.components.dashboard', [])
        .component('sfDashboard', {
            templateUrl: 'app-ajs/components/dashboards/dashboard/dashboard.html',
            controller,
            controllerAs: 'vm',
            bindings: {
                dashboard: '<',
                dashboardFilter: '<'
            }
        });

    function controller($document, $mdDialog, $state, $window, $timeout, model, dashboardService, filterService, reportService, pipelines, sfMoveReportDialog, $translate, $sce, $sanitize) {

        const vm = this;

        vm.getAddReportEmptyStateHtml = function () {
            const raw = $translate.instant('<a href="">Add reports</a> to build your dashboard');
            return $sce.trustAsHtml($sanitize(raw));
        };

        vm.handleAddReportEmptyStateClick = function ($event) {
            if ($event.target.tagName === 'A') {
                $event.preventDefault();
                vm.addReport();
            }
        };

        vm.unrenderedReports = [];
        vm.scoreCards = [];
        vm.charts = [];
        vm.canEditDashboards = ['Admin', 'Manager'].includes(model.me.role.name);
        vm.noDashboardTimeFilterText = ' (' + $translate.instant('dashboard time filter not applied') + ')';
        vm.reportCurrencyHtml = model.me.team.currency.html;

        let dashboardId;

        const reportTranslations = {
            'revenue': {
                title: $translate.instant('Revenue'),
                description: $translate.instant('The total value of your won opportunities.')
            },
            'avg-sales-cycle': {
                title: $translate.instant('Avg sales cycle'),
                description: $translate.instant('The average number of days until you close a deal, counting from the day you create it (customizable by setting the predefined custom field "start date"). You should strive to keep this as short as possible.')
            },
            'avg-won-value': {
                title: $translate.instant('Avg value'),
                description: $translate.instant('The average value of a won opportunity. Increasing this slightly usually doesn\'t increase the amount of sales effort needed, so it\'s good to try to increase this number to get a higher sales ROI.')
            },
            'won-count': {
                title: $translate.instant('# won'),
                description: $translate.instant('The number of opportunities you\'ve won. The more, the merrier!')
            },
            'new-count': {
                title: $translate.instant('# new'),
                description: $translate.instant('The number of new opportunities you\'ve created. It\'s important to keep filling up your pipeline constantly.')
            },
            'revenue-vs-goal': {
                title: $translate.instant('Expected revenue vs revenue goal'),
                description: $translate.instant('This report shows whether you\'re expected to hit your targets. The won revenue is the total value of your won opportunities. The expected revenue is calculated by summing value x probability for the rest of your opportunities. The black line in the graph is an indication of your target. Your won revenue plus expected revenue is compared with the target to estimate whether you\'ll hit it... or need to do some extra effort.')
            },
            'revenue-vs-prev-period': {
                title: $translate.instant('Revenue during time period'),
                description: $translate.instant('This report shows how your won revenue is distributed during the selected time period (by close date). It also compares this to the revenue distribution in the previous time period. Hover over the report to explore the details.')
            },
            'top-earning-accounts': {
                title: $translate.instant('Top earning accounts'),
                description: $translate.instant('These are the accounts that generated the most revenue for you in the selected time period. This is calculated by summing the value of the won opportunities per account. Accounts are then ranked in descending order.')
            },
            'top-lead-sources': {
                title: $translate.instant('Top lead sources'),
                description: $translate.instant('Your top sources for new leads. You can customize the options in Settings > Customize fields > Opportunities > Lead source > Edit. Then just fill out this field when creating a new opportunity to start tracking.')
            },
            'top-lost-reasons': {
                title: $translate.instant('Top lost reasons'),
                description: $translate.instant('The most common reasons why you\'ve lost an opportunity. You can customize the options in Settings > Customize fields > Opportunities > Lost reasons > Edit. You\'ll be asked to fill out a lost reason whenever you mark an opportunity as lost, unless you hide the field on the mentioned Settings page.')
            },
            'funnel-analysis': {
                title: $translate.instant('Funnel analysis'),
                description: $translate.instant('The funnel analysis gives you a good idea of how your opportunities move through the pipeline/funnel. For all opportunities closed in the selected period, it calculates how many opportunities you needed in each preceding stage to get this result. Additionally, it calculates how long opportunities stayed in each stage on average.')
            },
            'mrr': {
                title: $translate.instant('Monthly recurring revenue'),
                description: $translate.instant('Your Monthly Recurring Revenue (MRR) is the amount of recurring revenue generated on a monthly basis by all your won opportunities between their contract start date and end date. If no end date is set, the average customer lifetime is used to calculate it.')
            },
            'most-active-rep': {
                title: $translate.instant('Most active teammate'),
                description: $translate.instant('Who has the highest total of tracked emails, phone calls, and meetings? This is your most active teammate.')
            },
            'most-sold-by-rep': {
                title: $translate.instant('Most sold by teammate'),
                description: $translate.instant('The teammate who generated the most revenue (i.e. total value from won opportunities).')
            },
            'avg-sent-mails': {
                title: $translate.instant('Avg sent mails'),
                description: $translate.instant('The average number of tracked emails sent per teammate.')
            },
            'avg-meetings': {
                title: $translate.instant('Avg meetings'),
                description: $translate.instant('The average number of meetings held per teammate.')
            },
            'avg-calls': {
                title: $translate.instant('Avg calls'),
                description: $translate.instant('The average number of calls logged per teammate.')
            },
            'quota-per-rep': {
                title: $translate.instant('Leaderboard versus quota'),
                description: $translate.instant('This report shows how each teammate is performing versus their revenue target/quota in the selected time period. When the amount of revenue closed (horizontal bar) exceeds the target in the time period (black line), the bar changes from red to green.')
            },
            'closing-percentage-per-rep': {
                title: $translate.instant('Closing % per teammate'),
                description: $translate.instant('This report shows which teammates close the highest percentage of opportunities (of the ones created) as won. The black line in the report is the team average.')
            },
            'activity-per-rep': {
                title: $translate.instant('Activity per teammate'),
                description: $translate.instant('This report shows the amount of tracked emails, calls and meetings per teammate. The average per teammate is shown at the top for reference.')
            },
            'slipping-deals': {
                title: $translate.instant('Slipping opportunities'),
                description: $translate.instant('Forgetting to follow up? This report lists the accounts with the oldest "last interaction date" that have an open opportunity linked to them. Click through to check out the account and follow up.')
            },
            'closing-percentage': {
                title: $translate.instant('Closing %'),
                description: $translate.instant('This report shows the distribution of where team members are in terms of closing percentage versus each other. Hover over the bars to get more details.')
            }
        };

        vm.$onInit = function () {

            if (vm.dashboard) {
                dashboardId = vm.dashboard && vm.dashboard.id;
                filterService.setCurrentDashboardId(dashboardId);
                setDocumentTitle();
                return getReports();
            }
            else {
                dashboardId = $state.params.id;
                filterService.setCurrentDashboardId(dashboardId);
                return getDashboardAndReports();
            }
        };

        function setDocumentTitle() {
            $document[0].title = ($translate.instant('Insights') + ' ' + vm.dashboard.name + ' | Salesflare');
        }

        function getReports() {
            return dashboardService.getReports(dashboardId).then(function (response) {

                const reports = response.data;
                vm.unrenderedReports =  [...reports];

                if (!vm.dashboardFilter.pipeline?.recurring) {
                    vm.unrenderedReports = vm.unrenderedReports.filter(function (report) {

                        return report.predefined_type !== 'mrr';
                    });
                }

                vm.scoreCards = reports.filter(function (report) {

                    return report.chart_type === 'scorecard' || (report.chart_type === 'predefined' && reportService.predefinedScoreCards.includes(report.predefined_type));
                }).map((scoreCard) => {
                    return {
                        ...scoreCard,
                        title: reportTranslations[scoreCard.predefined_type]?.title || scoreCard.title,
                        description: reportTranslations[scoreCard.predefined_type]?.description || scoreCard.description
                    };
                });
                vm.charts = reports.filter(function (report) {

                    return (report.chart_type !== 'scorecard' && report.chart_type !== 'predefined') || (report.chart_type === 'predefined' && reportService.predefinedCharts.includes(report.predefined_type));
                }).map((chart) => {
                    return {
                        ...chart,
                        title: reportTranslations[chart.predefined_type]?.title || chart.title,
                        description: reportTranslations[chart.predefined_type]?.description || chart.description
                    };
                });

                if ($state.current.name === 'printDashboard') {
                    vm.showSpinner = true;
                }

                vm.doneLoading = true;
            });
        }

        function getDashboardAndReports() {
            return dashboardService.get(dashboardId).then(function (dashboardResponse) {
                vm.dashboard = dashboardResponse.data;
                setDocumentTitle();
                return pipelines.getViewable().then(function (pipelinesResponse) {

                    const currentPipelineId = store.get('current_pipeline_' + model.me.id);
                    let currentPipeline;

                    if (currentPipelineId) {
                        currentPipeline = pipelinesResponse.pipelines.find(function (pipeline) {

                            return pipeline.id === currentPipelineId;
                        });
                    }

                    const insightsFilterCopy = angular.copy(filterService.filters.insights);
                    insightsFilterCopy.pipeline = currentPipeline;

                    if (insightsFilterCopy.owner.type === 'user') {
                        insightsFilterCopy.user = insightsFilterCopy.owner.id;
                    }
                    else {
                        insightsFilterCopy.group = insightsFilterCopy.owner.id;
                    }

                    vm.dashboardFilter = insightsFilterCopy;

                    getReports();
                });
            });
        }

        vm.$onChanges = function (changes) {

            vm.reportCurrencyHtml = vm.dashboardFilter?.pipeline?.currency?.html || model.me.team.currency.html;

            if (changes.dashboard && changes.dashboard.currentValue) {
                setDocumentTitle();
            }
        };

        vm.afterRender = (reportId) => {
            if ($state.current.name === 'printDashboard') {
                if (vm.unrenderedReports.length > 0) {
                    vm.unrenderedReports = vm.unrenderedReports.filter(function (report) {

                        return report.id !== reportId;
                    });
                    if (vm.unrenderedReports.length === 0) {

                        vm.showSpinner = false;
                        $window.scrollTo({ top: $window.document.body.scrollHeight, behavior: 'smooth' });
                        $timeout(function () {

                            $window.print();
                        }, 1000);
                    }
                }
            }
        };

        vm.addReport = function () {

            analytics.track('Create report started', { gaClientId: model.gaClientId });

            return $state.go('createReport', { dashboardId });
        };

        vm.editReport = function (reportId) {

            return $state.go('editReport', { id: reportId });
        };

        vm.downloadReport = function (report) {

            const titleHeight = 44;
            const reportContainerElement = $document[0].querySelector('#report-container-' + report.id);
            let scrollElement = $document[0].querySelector('#report-' + report.id);
            if (!(report.chart_type === 'predefined' || report.chart_type === 'scorecard' || report.chart_type === 'table')) {
                scrollElement = $document[0].querySelector('#canvas-' + report.id);
            }

            html2canvas(reportContainerElement, {
                useCORS: true,
                height: scrollElement.scrollHeight + titleHeight,
                onclone: function (clone) {
                    clone.querySelector('#report-container-' + report.id).style.height = (scrollElement.scrollHeight + titleHeight) + 'px';
                }
            }).then(function (canvasElement) {
                downloadCanvas(canvasElement, report.title);
            });
        };

        function downloadCanvas(canvasElement, name) {
            // Set white background
            const ctx = canvasElement.getContext('2d');
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#FFF';
            ctx.fillRect(0, 0, canvasElement.width, canvasElement.height);

            const MIME_TYPE = 'image/png';

            const imgURL = canvasElement.toDataURL(MIME_TYPE);

            const dlLink = $document[0].createElement('a');
            dlLink.download = name + '.png';
            dlLink.href = imgURL;
            dlLink.dataset.downloadurl = [MIME_TYPE, dlLink.download, dlLink.href].join(':');

            $document[0].body.append(dlLink);
            dlLink.click();
            dlLink.remove();
        }

        vm.duplicateReport = function (reportId) {

            return $state.go('createReport', { dashboardId, reportId });
        };

        vm.moveReport = function (reportId) {

            const options = {
                reportId
            };

            return sfMoveReportDialog.show(options).then(function (newDashboardId) {

                return reportService.update(reportId, { dashboard: newDashboardId }).then(function () {

                    if (dashboardId !== newDashboardId) {
                        removeReportFromView(reportId);
                    }
                });
            });
        };

        vm.deleteReport = function (reportId) {

            const confirm = $mdDialog.confirm({ multiple: true })
                .clickOutsideToClose(true)
                .escapeToClose(true)
                .title($translate.instant('Are you sure you want to delete this report?'))
                .ok($translate.instant('I\'m sure'))
                .cancel($translate.instant('Cancel'));

            return $mdDialog.show(confirm).then(function () {

                return reportService.delete(reportId).then(function () {

                    return removeReportFromView(reportId);
                });
            });
        };

        function removeReportFromView(reportId) {

            const removeScoreCardIndex = vm.scoreCards.map(function (scoreCard) {

                return scoreCard.id;
            }).indexOf(reportId);
            const removeChartIndex = vm.charts.map(function (chart) {

                return chart.id;
            }).indexOf(reportId);

            if (removeChartIndex >= 0) {
                vm.charts.splice(removeChartIndex, 1);
            }

            if (removeScoreCardIndex >= 0) {
                vm.scoreCards.splice(removeScoreCardIndex, 1);
            }
        }
    }
})();
